// -*- coding: utf-8 -*-
import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import MessageStatusTicks from '../MessageStatusTicks';
import EmptyState from './EmptyState';

function renderWhatsAppBold(text) {
  const str = String(text || '');
  const parts = str.split(/(\*[^*\n]+\*)/g);
  return parts.map((part, idx) => {
    const isBoldToken = part.length >= 3 && part.startsWith('*') && part.endsWith('*') && !part.slice(1, -1).includes('*');
    if (isBoldToken) {
      return <strong key={`b-${idx}`}>{part.slice(1, -1)}</strong>;
    }
    return <React.Fragment key={`t-${idx}`}>{part}</React.Fragment>;
  });
}

function getReactionEmoji(message) {
  return String(message?.meta?.message?.reaction?.emoji || '').trim();
}

function getReplySelection(message) {
  const button = message?.meta?.message?.button;
  if (button) {
    const text = String(button.text || '').trim();
    const payload = String(button.payload || '').trim();
    return {
      kind: 'button',
      title: text || payload,
      detail: text && payload && payload !== text ? payload : '',
    };
  }

  const interactive = message?.meta?.message?.interactive;
  const buttonReply = interactive?.button_reply;
  if (buttonReply) {
    const title = String(buttonReply.title || '').trim();
    const id = String(buttonReply.id || '').trim();
    return {
      kind: 'interactive_button',
      title: title || id,
      detail: title && id && id !== title ? id : '',
    };
  }

  const listReply = interactive?.list_reply;
  if (listReply) {
    const title = String(listReply.title || '').trim();
    const id = String(listReply.id || '').trim();
    const description = String(listReply.description || '').trim();
    return {
      kind: 'interactive_list',
      title: title || id,
      detail: description || (title && id && id !== title ? id : ''),
    };
  }

  return null;
}

function getMessagePresentation(message) {
  if (message?.message_type === 'reaction') {
    const emoji = getReactionEmoji(message);
    return {
      isReaction: true,
      emoji,
      body: emoji ? 'Mesaja emoji reaksiyonu bıraktı' : 'Mesaja reaksiyon bıraktı',
    };
  }

  if (message?.message_type === 'button' || message?.message_type === 'interactive') {
    const selection = getReplySelection(message);
    if (selection?.title) {
      return {
        isReaction: false,
        isReplySelection: true,
        emoji: '',
        title: 'Buton yanıtı',
        body: selection.title,
        detail: selection.detail,
      };
    }
  }

  return {
    isReaction: false,
    isReplySelection: false,
    emoji: '',
    title: '',
    body: String(message?.body || ''),
    detail: '',
  };
}

export default function ChatMessages({
  selected,
  loading,
  history,
  formatTimestamp,
  palette,
  scrollRef,
  onLoadOlder,
  onIsAtBottomChange,
  onShowScrollToLatestChange,
  showScrollToLatest,
  nearBottomThreshold = 48,
  topFetchThreshold = 40,
  bubbleMaxWidth = '76%',
  bubblePadding = { px: 1.75, py: 1.25 },
  skeletonWidth = 220,
  withShadow = true,
}) {
  const handleScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop <= topFetchThreshold) {
      onLoadOlder();
    }
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < nearBottomThreshold;
    onIsAtBottomChange(nearBottom);
    if (nearBottom) onShowScrollToLatestChange(false);
  };

  return (
    <Box ref={scrollRef} sx={{ flex: 1, overflow: 'auto', pr: 0.5 }} onScroll={handleScroll}>
      {!selected && <EmptyState message="Bir veli seçin" />}
      {selected && loading && (
        <Box sx={{ px: 2, pt: 2 }}>
          {[...Array(4)].map((_, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: i % 2 ? 'flex-end' : 'flex-start', mb: 1.5 }}>
              <Skeleton variant="rounded" width={skeletonWidth + (i % 2 ? 60 : 0)} height={52} sx={{ bgcolor: 'action.disabledBackground' }} />
            </Box>
          ))}
        </Box>
      )}
      {selected && !loading && history.length === 0 && <EmptyState message="Henüz mesaj yok" />}
      {selected &&
        !loading &&
        history.length > 0 &&
        history.map((m, idx) => {
          const outgoing = m.direction === 'out';
          const presentation = getMessagePresentation(m);
          const prev = history[idx - 1];
          const sameSender = prev && prev.direction === m.direction;
          const within2min = prev && Math.abs(new Date(m.timestamp) - new Date(prev.timestamp)) / 60000 < 2;
          const compactTop = sameSender && within2min;
          return (
            <Box key={m.id} sx={{ mt: compactTop ? 0.5 : 1.25, mb: 1.0, display: 'flex', justifyContent: outgoing ? 'flex-end' : 'flex-start' }}>
              <Tooltip title={formatTimestamp(m.timestamp)} placement={outgoing ? 'left' : 'right'} arrow>
                <Box
                  sx={{
                    maxWidth: bubbleMaxWidth,
                    bgcolor: outgoing ? palette.OUT_BG : palette.IN_BG,
                    color: palette.TEXT,
                    px: bubblePadding.px,
                    py: bubblePadding.py,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: outgoing ? palette.OUT_BORDER : palette.IN_BORDER,
                    boxShadow: withShadow
                      ? outgoing
                        ? '0 1px 2px rgba(0,0,0,0.3)'
                        : '0 1px 2px rgba(0,0,0,0.25)'
                      : 'none',
                    wordBreak: 'break-word',
                    position: 'relative',
                  }}
                >
                  {presentation.isReaction ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 0.25, opacity: m.status === 'pending' ? 0.8 : 1 }}>
                      <Typography variant="h5" component="span" sx={{ lineHeight: 1 }}>
                        {presentation.emoji || '🙂'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.TEXT, whiteSpace: 'pre-line' }}>
                        {presentation.body}
                      </Typography>
                    </Box>
                  ) : presentation.isReplySelection ? (
                    <Box sx={{ mb: 0.25, opacity: m.status === 'pending' ? 0.8 : 1 }}>
                      <Typography variant="caption" sx={{ display: 'block', color: palette.TIMESTAMP, mb: 0.35, fontWeight: 600, letterSpacing: 0.2 }}>
                        {presentation.title}
                      </Typography>
                      <Typography variant="body2" sx={{ color: palette.TEXT, whiteSpace: 'pre-line' }}>
                        {presentation.body}
                      </Typography>
                      {presentation.detail ? (
                        <Typography variant="caption" sx={{ display: 'block', color: palette.TIMESTAMP, mt: 0.5, whiteSpace: 'pre-line' }}>
                          {presentation.detail}
                        </Typography>
                      ) : null}
                    </Box>
                  ) : (
                    <Typography variant="body2" sx={{ mb: 0.25, color: palette.TEXT, whiteSpace: 'pre-line', opacity: m.status === 'pending' ? 0.8 : 1 }}>
                      {renderWhatsAppBold(presentation.body)}
                    </Typography>
                  )}
                  <Typography variant="caption" sx={{ color: palette.TIMESTAMP, display: 'block', textAlign: 'right' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      {new Date(m.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', hour12: false })}
                      {outgoing && <MessageStatusTicks status={m.status} size={14} grey={palette.TIMESTAMP} />}
                    </span>
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          );
        })}
      {showScrollToLatest && (
        <Box sx={{ position: 'sticky', bottom: 8, display: 'flex', justifyContent: 'center' }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => {
              if (scrollRef.current) {
                scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
                onShowScrollToLatestChange(false);
                onIsAtBottomChange(true);
              }
            }}
          >
            Yeni mesajlar ↓
          </Button>
        </Box>
      )}
    </Box>
  );
}
